import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../components/Modal";
import {
  createLlmTestCase,
  getLlmTestCase,
  listClientForms,
  listLlmTestCases,
  listScenarios,
  runLlmTestCase,
  runLlmTestCasesBulk,
  listUserMoods,
  listUserScenarios,
  updateLlmTestCase,
} from "../api";

const BTN_GHOST =
  "inline-flex items-center justify-center rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-[#2a3246] bg-[#121a2a] px-4 py-2 text-sm font-semibold hover:bg-[#172036] disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_PAGER =
  "inline-flex items-center justify-center rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm font-semibold hover:bg-[#121825] disabled:opacity-50 disabled:cursor-not-allowed";
const INP =
  "w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-violet-600/40 focus:border-violet-500/40";
const SWITCH_BASE =
  "relative inline-flex h-6 w-11 items-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:cursor-not-allowed";
const SWITCH_KNOB =
  "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white transition";

const EMPTY_FORM = {
  name: "",
  description: "",
  assistant_scenario_id: "",
  client_id: "",
  user_scenario_id: "",
  user_mood_id: "",
  goal: "",
  max_turns: "20",
  max_duration_sec: "300",
  active: true,
};

const EMPTY_OPTIONS = {
  assistantScenarios: [],
  clients: [],
  userScenarios: [],
  userMoods: [],
};
const MAX_BULK_SELECTION = 50;

function dash(v) {
  return v == null || String(v).trim() === "" ? "—" : String(v);
}

function pickId(x) {
  return String(x?.doc_id ?? x?.docId ?? x?.id ?? x?._id ?? "").trim();
}
function formatNumber(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("bg-BG").format(n);
}

function toNamedOptions(items) {
  return (Array.isArray(items) ? items : [])
    .map((x) => {
      const id = pickId(x);
      if (!id) return null;
      const label = String(x?.name ?? id).trim() || id;
      return { value: id, label };
    })
    .filter(Boolean);
}

function readClientPersonal(client) {
  const personal =
    client?.personal ??
    client?.payload?.personal ??
    client?.form?.personal ??
    client?.client_payload?.personal ??
    {};
  return {
    firstName: String(personal?.firstName ?? personal?.first_name ?? "").trim(),
    lastName: String(personal?.lastName ?? personal?.last_name ?? "").trim(),
    egn: String(personal?.egn ?? "").trim(),
  };
}

function toClientOptions(items) {
  const arr = Array.isArray(items) ? items : [];
  const map = new Map();

  arr.forEach((x) => {
    const id = pickId(x);
    if (!id || map.has(id)) return;

    const p = readClientPersonal(x);
    const fullName = `${p.firstName} ${p.lastName}`.trim();
    let label = id;

    if (fullName && p.egn) label = `${fullName} (${p.egn})`;
    else if (fullName) label = fullName;
    else if (p.egn) label = `ЕГН: ${p.egn}`;

    map.set(id, { value: id, label });
  });

  return Array.from(map.values());
}

function withSelectedFallback(options, selectedId) {
  const id = String(selectedId || "").trim();
  if (!id) return options;
  if ((options || []).some((x) => x?.value === id)) return options;
  return [{ value: id, label: `${id} (липсва в списъка)` }, ...(options || [])];
}

async function listAllClientsForSelect() {
  const perPage = 200;
  let page = 1;
  const all = [];

  while (page <= 100) {
    const data = await listClientForms({ page, perPage, q: "" });
    const arr = Array.isArray(data) ? data : data?.items || [];
    all.push(...arr);

    if (Array.isArray(data)) break;

    const totalPages = Number(data?.totalPages ?? data?.total_pages ?? 0);
    const hasTotalPages = Number.isFinite(totalPages) && totalPages > 0;

    if (hasTotalPages) {
      if (page >= totalPages) break;
    } else if (arr.length < perPage) {
      break;
    }

    page += 1;
  }

  const uniq = new Map();
  all.forEach((x) => {
    const id = pickId(x);
    if (!id || uniq.has(id)) return;
    uniq.set(id, x);
  });
  return Array.from(uniq.values());
}

export default function LLMTestCases() {
  const runningIdsRef = useRef(new Set());
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [runErr, setRunErr] = useState("");
  const [runningMap, setRunningMap] = useState({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState("");

  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsErr, setOptionsErr] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editorDocId, setEditorDocId] = useState("");
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorErr, setEditorErr] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  const assistantScenarioOptions = useMemo(
    () => withSelectedFallback(options?.assistantScenarios || [], form?.assistant_scenario_id),
    [options?.assistantScenarios, form?.assistant_scenario_id]
  );
  const clientOptions = useMemo(
    () => withSelectedFallback(options?.clients || [], form?.client_id),
    [options?.clients, form?.client_id]
  );
  const userScenarioOptions = useMemo(
    () => withSelectedFallback(options?.userScenarios || [], form?.user_scenario_id),
    [options?.userScenarios, form?.user_scenario_id]
  );
  const userMoodOptions = useMemo(
    () => withSelectedFallback(options?.userMoods || [], form?.user_mood_id),
    [options?.userMoods, form?.user_mood_id]
  );
  const selectedIds = useMemo(
    () => Object.keys(selectedMap).filter((id) => !!selectedMap[id]),
    [selectedMap]
  );
  const selectedCount = selectedIds.length;
  const hasReachedSelectionLimit = selectedCount >= MAX_BULK_SELECTION;
  const canGoPrev = page > 1 && !loadingList;
  const canGoNext = page < totalPages && !loadingList;

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setListErr("");
        setLoadingList(true);
        const result = await listLlmTestCases({ page, perPage });
        if (!alive) return;
        const sourceItems = Array.isArray(result?.items) ? result.items : [];
        const nextPage = Math.max(1, Number(result?.page || page));
        const nextTotalPages = Math.max(1, Number(result?.totalPages || 1));
        const nextTotal = Number.isFinite(Number(result?.total)) ? Number(result.total) : sourceItems.length;

        setItems(sourceItems);
        setTotal(nextTotal);
        setTotalPages(nextTotalPages);
        if (nextPage !== page) setPage(nextPage);
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setTotal(0);
        setTotalPages(1);
        setListErr(e?.message || "Грешка при зареждане на тест кейсовете.");
      } finally {
        if (alive) setLoadingList(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [page, perPage, reloadKey]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setOptionsErr("");
        setOptionsLoading(true);

        const [assistantRows, clientsRows, userScenarioRows, userMoodRows] = await Promise.all([
          listScenarios(),
          listAllClientsForSelect(),
          listUserScenarios(),
          listUserMoods(),
        ]);

        if (!alive) return;
        setOptions({
          assistantScenarios: toNamedOptions(assistantRows),
          clients: toClientOptions(clientsRows),
          userScenarios: toNamedOptions(userScenarioRows),
          userMoods: toNamedOptions(userMoodRows),
        });
      } catch (e) {
        if (!alive) return;
        setOptions(EMPTY_OPTIONS);
        setOptionsErr(e?.message || "Грешка при зареждане на dropdown данните.");
      } finally {
        if (alive) setOptionsLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!editorOpen || editorMode !== "edit" || !editorDocId) return;
    let alive = true;

    const run = async () => {
      try {
        setEditorErr("");
        setEditorLoading(true);
        const data = await getLlmTestCase(editorDocId);
        if (!alive) return;

        setForm({
          name: String(data?.name ?? ""),
          description: String(data?.description ?? ""),
          assistant_scenario_id: String(data?.assistant_scenario_id ?? ""),
          client_id: String(data?.client_id ?? ""),
          user_scenario_id: String(data?.user_scenario_id ?? ""),
          user_mood_id: String(data?.user_mood_id ?? ""),
          goal: String(data?.goal ?? ""),
          max_turns: String(data?.max_turns ?? "20"),
          max_duration_sec: String(data?.max_duration_sec ?? "300"),
          active: !!data?.active,
        });
      } catch (e) {
        if (!alive) return;
        setEditorErr(e?.message || "Грешка при зареждане на детайли.");
      } finally {
        if (alive) setEditorLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [editorDocId, editorMode, editorOpen]);

  const openCreate = () => {
    setEditorMode("create");
    setEditorDocId("");
    setForm({ ...EMPTY_FORM });
    setEditorErr("");
    setEditorLoading(false);
    setEditorSaving(false);
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    const id = pickId(row);
    if (!id) return;
    setEditorMode("edit");
    setEditorDocId(id);
    setForm({ ...EMPTY_FORM });
    setEditorErr("");
    setEditorLoading(true);
    setEditorSaving(false);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (editorSaving) return;
    setEditorOpen(false);
    setEditorErr("");
    setEditorLoading(false);
  };

  const handleRun = async (row) => {
    const id = pickId(row);
    if (!id) {
      setRunErr("Липсва ID на тест кейса.");
      return;
    }

    if (runningIdsRef.current.has(id)) return;
    runningIdsRef.current.add(id);
    setRunningMap((prev) => ({ ...prev, [id]: true }));

    try {
      setRunErr("");
      await runLlmTestCase(id);
      setToast("Тестът е стартиран.");
    } catch (e) {
      setRunErr(e?.message || "Грешка при стартиране на теста.");
    } finally {
      runningIdsRef.current.delete(id);
      setRunningMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleToggleSelected = (row, e) => {
    e?.stopPropagation?.();
    const id = pickId(row);
    if (!id || bulkRunning) return;
    if (selectedMap[id]) {
      setSelectedMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }

    if (row?.active !== true) return;
    if (hasReachedSelectionLimit) {
      setToast(`Можеш да избереш максимум ${MAX_BULK_SELECTION} тест кейса.`);
      return;
    }

    setSelectedMap((prev) => ({ ...prev, [id]: true }));
  };

  const handleBulkRun = async () => {
    if (bulkRunning) return;

    const ids = selectedIds;
    if (ids.length === 0) {
      setRunErr("Няма избрани active тест кейсове.");
      return;
    }
    if (ids.length > MAX_BULK_SELECTION) {
      setToast(`Можеш да стартираш максимум ${MAX_BULK_SELECTION} тест кейса наведнъж.`);
      return;
    }

    try {
      setRunErr("");
      setBulkRunning(true);
      await runLlmTestCasesBulk(ids);
      setToast("Избраните тестове са стартирани.");
      setSelectedMap({});
      setReloadKey((k) => k + 1);
    } catch (e) {
      setRunErr(e?.message || "Грешка при масово стартиране на тестове.");
    } finally {
      setBulkRunning(false);
    }
  };

  const validate = () => {
    if (!String(form?.name || "").trim()) return "Името е задължително.";
    if (!String(form?.description || "").trim()) return "Описанието е задължително.";
    if (!String(form?.assistant_scenario_id || "").trim()) return "Assistant сценарият е задължителен.";
    if (!String(form?.client_id || "").trim()) return "Длъжникът е задължителен.";
    if (!String(form?.user_scenario_id || "").trim()) return "Потребителският сценарий е задължителен.";
    if (!String(form?.user_mood_id || "").trim()) return "Потребителското настроение е задължително.";
    if (!String(form?.goal || "").trim()) return "Goal е задължително поле.";

    const maxTurns = Number(form?.max_turns);
    if (!Number.isInteger(maxTurns) || maxTurns < 1) {
      return "max_turns трябва да е положително цяло число.";
    }

    const maxDuration = Number(form?.max_duration_sec);
    if (!Number.isInteger(maxDuration) || maxDuration < 1) {
      return "max_duration_sec трябва да е положително цяло число.";
    }

    return "";
  };

  const saveEditor = async () => {
    const validationErr = validate();
    if (validationErr) {
      setEditorErr(validationErr);
      return;
    }

    const payload = {
      name: String(form?.name || "").trim(),
      description: String(form?.description || "").trim(),
      assistant_scenario_id: String(form?.assistant_scenario_id || "").trim(),
      client_id: String(form?.client_id || "").trim(),
      user_scenario_id: String(form?.user_scenario_id || "").trim(),
      user_mood_id: String(form?.user_mood_id || "").trim(),
      goal: String(form?.goal || "").trim(),
      max_turns: Number(form?.max_turns),
      max_duration_sec: Number(form?.max_duration_sec),
      active: !!form?.active,
    };

    try {
      setEditorErr("");
      setEditorSaving(true);

      if (editorMode === "edit") {
        await updateLlmTestCase(editorDocId, payload);
        setToast("Тест кейсът е обновен.");
      } else {
        await createLlmTestCase(payload);
        setToast("Тест кейсът е създаден.");
      }

      setEditorOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setEditorErr(e?.message || "Грешка при запис.");
    } finally {
      setEditorSaving(false);
    }
  };

  const showBlockingListLoading = loadingList && items.length === 0;

  return (
    <>
      <div className="card rounded-2xl p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold whitespace-nowrap">Тест Кейсове</h2>
          <div className="ml-auto flex items-center gap-2">
            <button
              className={BTN_SECONDARY}
              onClick={handleBulkRun}
              disabled={loadingList || bulkRunning || selectedCount === 0}
            >
              {bulkRunning
                ? "Стартиране..."
                : "Стартирай избраните"}
            </button>
            <button className={BTN_PRIMARY} onClick={openCreate}>
            Добави тест кейс
            </button>
          </div>
        </div>

        {!!listErr && (
          <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
            {listErr}
          </div>
        )}
        {!!runErr && (
          <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
            {runErr}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {showBlockingListLoading ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-300">Няма добавени тест кейсове.</div>
          ) : (
            items.map((row, idx) => {
              const id = pickId(row);
              const isActive = row?.active === true;
              const isSelected = !!(id && selectedMap[id]);
              const isRunning = !!(id && runningMap[id]);
              const isSelectable = !!id && isActive;
              const isBlockedByLimit = !isSelected && hasReachedSelectionLimit;
              const isSelectionDisabled = bulkRunning || (!isSelected && (!isSelectable || isBlockedByLimit));
              const selectionTitle = isSelected
                ? "Премахни от избраните"
                : !isSelectable
                  ? "Неактивен тест"
                  : isBlockedByLimit
                    ? `Лимит: ${MAX_BULK_SELECTION}`
                    : "Избери за масов старт";
              return (
                <div
                  key={id || `${row?.name || "row"}-${idx}`}
                  className="w-full rounded-2xl border border-[#1f2638] bg-[#0f1523] px-4 py-3 transition hover:bg-[#121825]"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center pt-1">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isSelected}
                        aria-label="select test case"
                        disabled={isSelectionDisabled}
                        title={selectionTitle}
                        onClick={(e) => handleToggleSelected(row, e)}
                        className={[
                          SWITCH_BASE,
                          isSelected
                            ? "border-violet-500/60 bg-violet-600/50 justify-end px-1"
                            : "border-[#2b344a] bg-[#0a1020] justify-start px-1",
                          isSelectionDisabled ? "opacity-45" : "",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            SWITCH_KNOB,
                            isSelected ? "bg-violet-100" : "bg-slate-300",
                          ].join(" ")}
                        />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      className="min-w-0 flex-1 text-left rounded-lg p-1 -m-1 outline-none focus:ring-2 focus:ring-violet-600/40"
                    >
                      <div className="font-semibold text-slate-200 truncate">{dash(row?.name)}</div>
                      <div className="text-sm text-slate-400 truncate mt-0.5">
                        {dash(row?.description)}
                      </div>
                    </button>
                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        className={[
                          "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                          isActive
                            ? "border-emerald-600/40 bg-emerald-600/15 text-emerald-200"
                            : "border-slate-700 bg-slate-700/30 text-slate-300",
                        ].join(" ")}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRun(row)}
                        disabled={!isActive || isRunning || bulkRunning}
                        title={isActive ? "Стартирай теста" : "Неактивен тест"}
                        className={[
                          "inline-flex items-center justify-center rounded-xl border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition disabled:cursor-not-allowed",
                          isActive
                            ? "border-violet-500/40 bg-violet-600/20 text-violet-100 hover:bg-violet-600/30 disabled:opacity-60"
                            : "border-slate-700 bg-slate-700/30 text-slate-400",
                        ].join(" ")}
                      >
                        {isRunning ? "Стартиране..." : "Стартирай теста"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            className={BTN_PAGER}
            disabled={!canGoPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>
          <div className="text-sm text-slate-300 px-2">
            Страница {page} / {Math.max(1, totalPages)}
          </div>
          <button
            className={BTN_PAGER}
            disabled={!canGoNext}
            onClick={() => setPage((p) => p + 1)}
          >
            Напред
          </button>
          <div className="ml-auto text-xs text-slate-400">Общо: {formatNumber(total)}</div>
        </div>
      </div>

      <Modal
        open={editorOpen}
        title={editorMode === "edit" ? "Редакция на тест кейс" : "Добавяне на тест кейс"}
        onClose={closeEditor}
        scroll={false}
        panelClassName="max-w-4xl w-[94vw] md:w-[980px]"
        footer={
          <>
            <button className={BTN_GHOST} onClick={closeEditor} disabled={editorSaving}>
              Cancel
            </button>
            <button className={BTN_PRIMARY} onClick={saveEditor} disabled={editorSaving || editorLoading}>
              {editorSaving ? "Запис..." : "Запази"}
            </button>
          </>
        }
      >
        <div
          className="max-h-[70vh] overflow-auto pr-4 nice-scrollbar"
          style={{ scrollbarGutter: "stable both-edges" }}
        >
          {editorLoading ? (
            <div className="text-sm text-slate-300">Зареждане...</div>
          ) : (
            <div className="space-y-4">
              {!!editorErr && (
                <div className="rounded-xl border border-rose-600/30 bg-rose-600/10 px-4 py-3 text-sm text-rose-200">
                  {editorErr}
                </div>
              )}

              {!!optionsErr && (
                <div className="rounded-xl border border-amber-600/30 bg-amber-600/10 px-4 py-3 text-sm text-amber-100">
                  {optionsErr}
                </div>
              )}

              <label className="inline-flex items-center gap-2 text-sm text-slate-200 select-none">
                <input
                  type="checkbox"
                  checked={!!form?.active}
                  onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))}
                  disabled={editorSaving}
                  className="h-4 w-4 accent-violet-500"
                />
                Active
              </label>

              <div>
                <div className="text-xs text-slate-400 mb-1">Име</div>
                <input
                  className={INP}
                  value={form?.name || ""}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Име на тест кейса"
                  disabled={editorSaving}
                />
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-1">Описание</div>
                <textarea
                  className={[INP, "min-h-[96px] resize-y"].join(" ")}
                  value={form?.description || ""}
                  onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Описание"
                  disabled={editorSaving}
                  style={{ scrollbarGutter: "stable" }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-400 mb-1">Assistant сценарий</div>
                  <select
                    className={INP}
                    value={form?.assistant_scenario_id || ""}
                    onChange={(e) => setForm((s) => ({ ...s, assistant_scenario_id: e.target.value }))}
                    disabled={editorSaving || optionsLoading}
                  >
                    <option value="">
                      {optionsLoading ? "Зареждане..." : "Избери assistant сценарий"}
                    </option>
                    {assistantScenarioOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">Длъжник</div>
                  <select
                    className={INP}
                    value={form?.client_id || ""}
                    onChange={(e) => setForm((s) => ({ ...s, client_id: e.target.value }))}
                    disabled={editorSaving || optionsLoading}
                  >
                    <option value="">
                      {optionsLoading ? "Зареждане..." : "Избери длъжник"}
                    </option>
                    {clientOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">Потребителски сценарий</div>
                  <select
                    className={INP}
                    value={form?.user_scenario_id || ""}
                    onChange={(e) => setForm((s) => ({ ...s, user_scenario_id: e.target.value }))}
                    disabled={editorSaving || optionsLoading}
                  >
                    <option value="">
                      {optionsLoading ? "Зареждане..." : "Избери потребителски сценарий"}
                    </option>
                    {userScenarioOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">Потребителско настроение</div>
                  <select
                    className={INP}
                    value={form?.user_mood_id || ""}
                    onChange={(e) => setForm((s) => ({ ...s, user_mood_id: e.target.value }))}
                    disabled={editorSaving || optionsLoading}
                  >
                    <option value="">
                      {optionsLoading ? "Зареждане..." : "Избери потребителско настроение"}
                    </option>
                    {userMoodOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-1">Цел на разговора</div>
                <textarea
                  className={[INP, "min-h-[160px] resize-y"].join(" ")}
                  value={form?.goal || ""}
                  onChange={(e) => setForm((s) => ({ ...s, goal: e.target.value }))}
                  placeholder="Цел и критерии за успех..."
                  disabled={editorSaving}
                  style={{ scrollbarGutter: "stable" }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-400 mb-1">Макс реплики</div>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={INP}
                    value={form?.max_turns ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, max_turns: e.target.value }))}
                    disabled={editorSaving}
                  />
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Макс продължителност в сек.</div>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={INP}
                    value={form?.max_duration_sec ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, max_duration_sec: e.target.value }))}
                    disabled={editorSaving}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {!!toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[120]">
          <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 shadow-lg text-sm text-slate-200">
            {toast}
          </div>
        </div>
      )}

      {selectedCount > 0 && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 z-[110]">
          <div className="rounded-xl border border-violet-500/40 bg-[#0f1523]/95 px-5 py-3 shadow-lg text-sm text-violet-100">
            Избрани: {selectedCount}/{MAX_BULK_SELECTION}
          </div>
        </div>
      )}
    </>
  );
}
